import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, X, Loader2, Lock, HeartHandshake, LifeBuoy } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";

/**
 * One-time "keep project communication on Last Bookmark" notice for
 * Early Access members. Mounted from ``SiteShell`` so it fires on the
 * first authenticated page load regardless of route.
 *
 * Visibility rules:
 *   * user is signed in (not ephemeral demo visitor)
 *   * ``user.is_private_test === true`` (Early Access active)
 *   * ``user.early_access_comms_notice_ack_at`` is falsy
 *   * user is NOT an admin (admins already know)
 *
 * Acknowledgement is persisted server-side via
 * ``POST /api/private-tester-requests/me/ack-comms-notice``. After the
 * user taps "Got it", the modal is hidden for this session and the
 * refreshed ``/auth/me`` payload keeps it hidden on subsequent loads.
 */
export default function EarlyAccessCommsNoticeModal({ previewMode = false, onClose } = {}) {
  const { user, refresh } = useAuth();
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const isAdmin = (user?.roles || []).includes("admin");
  const isPT = !!user?.is_private_test;
  const alreadyAck = !!user?.early_access_comms_notice_ack_at;
  // Gate the informational notice so it renders only AFTER the user has
  // accepted the Early Access Community Agreement. This enforces the
  // required onboarding order (Agreement first → informational notice
  // second) without needing a second fetch: the acceptance timestamp is
  // already exposed on ``/auth/me``.
  const hasAcceptedAgreement = !!user?.early_access_agreement_accepted_at;

  if (!previewMode) {
    if (!user || isAdmin || !isPT || user.is_ephemeral) return null;
    if (alreadyAck || dismissed) return null;
    if (!hasAcceptedAgreement) return null;
  }

  const acknowledge = async () => {
    setSaving(true);
    try {
      await api.post("/private-tester-requests/me/ack-comms-notice", {});
      setDismissed(true);
      if (typeof refresh === "function") {
        try { await refresh(); } catch { /* silent */ }
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 sm:p-4 overflow-y-auto"
      data-testid="early-access-comms-notice-backdrop"
      role="dialog"
      aria-labelledby="early-access-comms-notice-title"
      aria-modal="true"
    >
      <div
        className="w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl bg-card shadow-2xl border max-h-[92vh] sm:max-h-[calc(100vh-4rem)] flex flex-col"
        data-testid="early-access-comms-notice-modal"
      >
        <div className="flex items-center gap-3 px-6 pt-5 pb-3 border-b bg-card rounded-t-2xl">
          <div className="w-9 h-9 rounded-full bg-[hsl(var(--sb-forest))] text-[hsl(42_60%_96%)] grid place-items-center shrink-0">
            <ShieldCheck size={18} />
          </div>
          <h3
            id="early-access-comms-notice-title"
            className="font-serif text-2xl leading-tight"
          >
            Keep the conversation on Last Bookmark
          </h3>
        </div>
        <div className="flex-1 overflow-y-auto px-6 pt-4 pb-4 space-y-4 text-sm md:text-[15px] leading-relaxed text-foreground/85">
          <div className="rounded-xl bg-[hsl(var(--sb-cream-2)/0.7)] border border-[hsl(var(--sb-forest))]/25 px-4 py-3 space-y-2">
            <p className="font-semibold text-foreground">During Early Access</p>
            <p>
              We encourage authors and beta readers to keep{" "}
              <strong>project-related conversations on Last Bookmark</strong>{" "}
              whenever possible so we can properly test and improve the
              platform. Reasonable external links or contact information
              may still be shared when necessary for manuscripts,
              portfolios, collaboration, or legitimate payment arrangements.
            </p>
          </div>
          <div className="rounded-xl bg-[hsl(var(--sb-cream-2)/0.7)] border border-[hsl(var(--sb-gold))]/40 px-4 py-3 space-y-2">
            <p className="font-semibold text-foreground flex items-center gap-2">
              <Lock size={14} className="text-[hsl(var(--sb-gold-2))]" />
              At full launch
            </p>
            <p>
              Paid beta-reading transactions arranged through Last Bookmark
              will be completed through Last Bookmark once integrated payments
              are available. Additional restrictions on sharing personal
              contact information or payment handles may be introduced as
              Last Bookmark's safety and payment features are finalized.
            </p>
          </div>
          <div>
            <p className="font-semibold text-foreground mb-2">Why we're asking</p>
            <ul className="space-y-2 list-none pl-0">
              <li className="flex gap-2.5">
                <HeartHandshake size={16} className="mt-0.5 shrink-0 text-[hsl(var(--sb-forest))]" />
                <span><strong>Privacy.</strong> Personal contact info shared off-platform can't be protected the way in-app messages can.</span>
              </li>
              <li className="flex gap-2.5">
                <ShieldCheck size={16} className="mt-0.5 shrink-0 text-[hsl(var(--sb-forest))]" />
                <span><strong>Accountability.</strong> Keeping project interactions organized means both sides have a clear record if expectations change.</span>
              </li>
              <li className="flex gap-2.5">
                <LifeBuoy size={16} className="mt-0.5 shrink-0 text-[hsl(var(--sb-forest))]" />
                <span><strong>Support.</strong> If anything goes wrong, we can only help when the conversation is on the platform.</span>
              </li>
            </ul>
          </div>
          <p className="text-xs text-muted-foreground">
            Nothing on your profile, projects, or messages is being changed
            right now — this is an informational notice for Early Access.
          </p>
        </div>
        <div
          className="flex justify-end px-6 py-4 border-t bg-card rounded-b-2xl"
          style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
        >
          {previewMode ? (
            <button
              type="button"
              onClick={() => onClose?.()}
              className="btn-primary w-full sm:w-auto"
              data-testid="early-access-comms-notice-preview-close"
            >
              Close preview
            </button>
          ) : (
            <button
              type="button"
              onClick={acknowledge}
              disabled={saving}
              className="btn-primary w-full sm:w-auto"
              data-testid="early-access-comms-notice-ack"
            >
              {saving ? <><Loader2 className="animate-spin" size={14} /> Saving…</> : <>Got it</>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
